import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/why-bridge-advisors", label: "Why Us" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <motion.header
        data-testid="main-nav"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-bone/80 backdrop-blur-xl border-b border-border"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 lg:px-12">
          <Link to="/" data-testid="nav-logo" className="group flex items-baseline gap-2">
            <span className={`font-serif text-xl tracking-tight transition-colors ${scrolled ? "text-obsidian" : "text-bone"}`}>Bridge Advisors</span>
            <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${scrolled ? "text-brand" : "text-bone/60"}`}>LLP</span>
          </Link>

          <div className="hidden items-center gap-9 lg:flex">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`link-underline text-sm font-medium transition-colors ${
                  scrolled
                    ? pathname === l.to ? "text-brand" : "text-obsidian hover:text-brand"
                    : pathname === l.to ? "text-bone" : "text-bone/70 hover:text-bone"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta"
              className={`group inline-flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors duration-300 ${
                scrolled ? "bg-obsidian text-bone hover:bg-brand" : "bg-bone text-obsidian hover:bg-brand hover:text-bone"
              }`}
            >
              Book a Consultation
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden ${scrolled || open ? "text-obsidian" : "text-bone"}`}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 flex flex-col justify-center gap-2 bg-bone px-8 lg:hidden"
          >
            {LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link
                  to={l.to}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  className="block border-b border-border py-5 font-serif text-4xl text-obsidian"
                >
                  {l.label}
                </Link>
              </motion.div>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta"
              className="mt-8 inline-flex items-center justify-center gap-2 bg-obsidian px-6 py-4 text-sm font-medium text-bone"
            >
              Book a Consultation <ArrowUpRight size={16} />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
